import { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import { format } from 'date-fns';
import { Search, ChevronDown, ChevronRight, ChevronLeft, User, Calendar, Smartphone, Monitor, Tablet, Globe, Download } from 'lucide-react';

interface Customer {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  _count: { scans: number };
}

interface CustomerDetail {
  customer: {
    id: string;
    email: string;
    name: string;
    createdAt: string;
    totalScans: number;
  };
  participation: Array<{
    campaignName: string;
    runNumber: number;
    firstScan: string;
    lastScan: string;
    totalScans: number;
  }>;
  recentScans: Array<{
    id: string;
    scannedAt: string;
    run: {
      id: string;
      instanceNumber: number;
      campaign: { id: string; name: string };
    };
    deviceType?: string;
    osName?: string;
    osVersion?: string;
    browserName?: string;
    browserVersion?: string;
    platform?: string;
    ipAddress?: string;
    city?: string;
    region?: string;
    country?: string;
  }>;
}

interface Campaign {
  id: string;
  name: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function CustomersPage() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [expandedCustomer, setExpandedCustomer] = useState<string | null>(null);
  const [customerDetail, setCustomerDetail] = useState<CustomerDetail | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<Pagination | null>(null);

  useEffect(() => {
    loadCampaigns();
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, campaignFilter, yearFilter]);

  useEffect(() => {
    loadCustomers();
  }, [search, campaignFilter, yearFilter, page]);

  const loadCampaigns = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: Campaign[] }>('/store/dashboard/campaigns');
      setCampaigns(response.data.data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  };

  const loadCustomers = async () => {
    setLoading(true);
    setExpandedCustomer(null);
    setCustomerDetail(null);

    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (campaignFilter) params.set('campaignId', campaignFilter);
      if (yearFilter) params.set('year', yearFilter);
      params.set('page', page.toString());
      params.set('limit', '50');

      const response = await apiClient.get<{ success: boolean; data: Customer[]; pagination: Pagination }>(
        `/store/dashboard/customers?${params.toString()}`
      );
      setCustomers(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCustomer = async (customerId: string) => {
    if (expandedCustomer === customerId) {
      setExpandedCustomer(null);
      setCustomerDetail(null);
      return;
    }

    setExpandedCustomer(customerId);
    try {
      const response = await apiClient.get<{ success: boolean; data: CustomerDetail }>(
        `/store/dashboard/customers/${customerId}`
      );
      setCustomerDetail(response.data.data);
    } catch (error) {
      console.error('Failed to load customer detail:', error);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const blob = await apiClient.getBlob('/store/customers/export');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customers.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export CSV:', error);
      alert('Failed to export customers. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const years = [2024, 2025, 2026, 2027];

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">My Customers</h1>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {pagination ? `${pagination.total} customers` : `${customers.length} customers`}
          </span>
          <button
            onClick={handleExportCSV}
            disabled={exporting}
            className="btn-secondary flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <select
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
            className="input"
          >
            <option value="">All Campaigns</option>
            {campaigns.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
            ))}
          </select>
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="input"
          >
            <option value="">All Years</option>
            {years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Customer List */}
      {loading ? (
        <div className="animate-pulse">Loading...</div>
      ) : customers.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">
          <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">No customers yet</p>
          <p className="text-sm mt-1">
            Customers will appear here once they scan your QR codes.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {customers.map((customer) => (
            <div key={customer.id} className="card">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => toggleCustomer(customer.id)}
              >
                <div className="flex items-center gap-4">
                  <button className="text-gray-400">
                    {expandedCustomer === customer.id ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                  </button>
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{customer.name}</p>
                    <p className="text-sm text-gray-500">{customer.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{customer._count.scans} scans</p>
                  <p className="text-sm text-gray-500">
                    Joined {format(new Date(customer.createdAt), 'MMM d, yyyy')}
                  </p>
                </div>
              </div>

              {/* Expanded Customer Detail */}
              {expandedCustomer === customer.id && customerDetail && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h4 className="font-medium text-gray-900 mb-3">Campaign Participation</h4>

                  {customerDetail.participation.length > 0 ? (
                    <div className="space-y-3 mb-6">
                      {customerDetail.participation.map((p, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{p.campaignName} (Run #{p.runNumber})</span>
                            <span className="text-sm text-gray-500">• {p.totalScans} scans</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            <span>First scan: {format(new Date(p.firstScan), 'MMM d, yyyy h:mm a')}</span>
                            {p.firstScan !== p.lastScan && (
                              <span> • Last scan: {format(new Date(p.lastScan), 'MMM d, yyyy h:mm a')}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm mb-4">No campaign participation data</p>
                  )}

                  <h4 className="font-medium text-gray-900 mb-3">Recent Scans</h4>
                  {customerDetail.recentScans.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="pb-2">Date/Time</th>
                            <th className="pb-2">Campaign</th>
                            <th className="pb-2">Device</th>
                            <th className="pb-2">Location</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {customerDetail.recentScans.map((scan) => (
                            <tr key={scan.id}>
                              <td className="py-2">
                                {format(new Date(scan.scannedAt), 'MMM d, yyyy h:mm a')}
                              </td>
                              <td className="py-2">
                                {scan.run.campaign.name} (Run #{scan.run.instanceNumber})
                              </td>
                              <td className="py-2">
                                <div className="flex items-center gap-2">
                                  {scan.deviceType === 'mobile' && <Smartphone className="w-4 h-4 text-gray-400" />}
                                  {scan.deviceType === 'tablet' && <Tablet className="w-4 h-4 text-gray-400" />}
                                  {scan.deviceType === 'desktop' && <Monitor className="w-4 h-4 text-gray-400" />}
                                  {!scan.deviceType && <Globe className="w-4 h-4 text-gray-400" />}
                                  <div className="text-xs">
                                    {scan.osName && (
                                      <span className="text-gray-700">
                                        {scan.osName}
                                        {scan.osVersion && ` ${scan.osVersion}`}
                                      </span>
                                    )}
                                    {scan.browserName && (
                                      <span className="text-gray-500 ml-1">
                                        {scan.browserName}
                                        {scan.browserVersion && ` ${scan.browserVersion.split('.')[0]}`}
                                      </span>
                                    )}
                                    {!scan.osName && !scan.browserName && (
                                      <span className="text-gray-400">Unknown</span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="py-2 text-xs text-gray-500">
                                {scan.city || scan.region || scan.country ? (
                                  <span>
                                    {[scan.city, scan.region, scan.country].filter(Boolean).join(', ')}
                                  </span>
                                ) : scan.ipAddress ? (
                                  <span className="text-gray-400">{scan.ipAddress}</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No recent scans</p>
                  )}
                </div>
              )}
            </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {pagination && pagination.totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {((page - 1) * pagination.limit) + 1} - {Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="btn-secondary p-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600 px-2">
                  Page {page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages}
                  className="btn-secondary p-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
